import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@material-ui/core'
import SyncIcon from '@material-ui/icons/Sync'
import SearchIcon from '@material-ui/icons/Search'
import FilterListIcon from '@material-ui/icons/FilterList'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'
import EventIcon from '@material-ui/icons/Event'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import WarningIcon from '@material-ui/icons/Warning'
import VisibilityIcon from '@material-ui/icons/Visibility'
import SettingsIcon from '@material-ui/icons/Settings'
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck'
import SendIcon from '@material-ui/icons/Send'
import GetAppIcon from '@material-ui/icons/GetApp'
import { useStyles } from './styles'
import { getStatusLabel, getStatusChip } from './statusUtils'
import { DocumentViewer } from '../LeaseAuditNew/Detail/ResidentTermsTab/DocumentViewer'
import { MoveOutDetail } from './MoveOutDetail'
import { FMOConfigDialog } from './FMOConfigDialog'
import { FinancialConnectionDialog } from './FinancialConnectionDialog'
import {
    useMoveOutManagement,
    useSyncTransactionsWithProgress,
    useBulkSendFmo,
    useBulkExportFmo,
    FinalMoveOutStatus,
    MoveOutOrdering,
    MoveOutQuickFilters,
} from '../../hooks/useMoveOutManagement/useMoveOutManagement'
import { useDebounce } from '../../hooks/useDebounce'
import { formatCurrency, formatDate } from './utils'

const DEFAULT_PAGE_SIZE = 25

const ALL_STATUSES: FinalMoveOutStatus[] = [
    'not_started',
    'pending_inspection',
    'inspection_complete',
    'fmo_sent',
    'fmo_reviewed',
    'fmo_accepted',
    'in_dispute',
    'final',
]

const formatRelativeTime = (isoDate: string): string => {
    const now = Date.now()
    const then = new Date(isoDate).getTime()
    const diffMs = now - then
    const diffMin = Math.floor(diffMs / 60_000)
    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    const diffDay = Math.floor(diffHr / 24)
    if (diffDay < 30) return `${diffDay}d ago`
    return formatDate(isoDate)
}

export default function MoveOutManagement() {
    const classes = useStyles()

    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilters, setStatusFilters] = useState<FinalMoveOutStatus[]>([])
    const [ordering, setOrdering] = useState<MoveOutOrdering>('-move_out_date')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [selectedLeaseId, setSelectedLeaseId] = useState<number | null>(null)
    const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(
        null,
    )
    const [viewingDocument, setViewingDocument] = useState<{
        id: number
        name: string
        url: string
    } | null>(null)

    const [configDialogOpen, setConfigDialogOpen] = useState(false)
    const [connectionDialogOpen, setConnectionDialogOpen] = useState(false)
    const [bulkActionsMode, setBulkActionsMode] = useState(false)
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
    const [quickFilters, setQuickFilters] = useState<MoveOutQuickFilters>({})

    const debouncedSearch = useDebounce(searchQuery, 400)

    const {
        startSync,
        isSyncing,
        progress: syncProgress,
        resetProgress,
    } = useSyncTransactionsWithProgress()

    const bulkSend = useBulkSendFmo()
    const bulkExport = useBulkExportFmo()

    const bulkSendDone = bulkSend.progress?.state === 'SUCCESS'
    const bulkSendFailed = bulkSend.progress?.state === 'FAILURE'
    const bulkExportDone = bulkExport.progress?.state === 'SUCCESS'
    const bulkExportFailed = bulkExport.progress?.state === 'FAILURE'

    useEffect(() => {
        if (bulkSendDone) {
            toast.success('Bulk send completed successfully.')
            setSelectedRows(new Set())
            bulkSend.resetProgress()
        } else if (bulkSendFailed) {
            toast.error(
                bulkSend.progress?.error ??
                    'Bulk send failed. Please try again.',
            )
            bulkSend.resetProgress()
        }
    }, [bulkSendDone, bulkSendFailed, bulkSend])

    useEffect(() => {
        if (bulkExportDone) {
            toast.success(
                'Export complete — you will receive an email with the exported PDFs shortly.',
            )
            setSelectedRows(new Set())
            bulkExport.resetProgress()
        } else if (bulkExportFailed) {
            toast.error(
                bulkExport.progress?.error ??
                    'Bulk export failed. Please try again.',
            )
            bulkExport.resetProgress()
        }
    }, [bulkExportDone, bulkExportFailed, bulkExport])

    const {
        moveOutLeases,
        count,
        summaryStats,
        isLoading,
        isFetching,
        error,
    } = useMoveOutManagement({
        page,
        pageSize,
        search: debouncedSearch,
        statuses: statusFilters,
        ordering,
        quickFilters,
    })

    const hasFinancialConnection =
        summaryStats?.entrata_ar_codes_available ?? false
    const lastSyncTime = summaryStats?.last_lease_transaction_sync ?? null

    const handlePageChange = useCallback((_: unknown, newPage: number) => {
        setPage(newPage + 1)
    }, [])

    const handlePageSizeChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setPageSize(parseInt(event.target.value, 10))
            setPage(1)
        },
        [],
    )

    const handleToggleStatus = useCallback((value: FinalMoveOutStatus) => {
        setStatusFilters((prev) =>
            prev.includes(value)
                ? prev.filter((s) => s !== value)
                : [...prev, value],
        )
        setPage(1)
    }, [])

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(e.target.value)
            setPage(1)
        },
        [],
    )

    const handleToggleQuickFilter = useCallback(
        (key: keyof MoveOutQuickFilters) => {
            setQuickFilters((prev) => {
                const next = { ...prev }
                if (next[key]) {
                    delete next[key]
                } else {
                    next[key] = true
                }
                return next
            })
            setPage(1)
        },
        [],
    )

    const sortField = ordering.replace(/^-/, '') as
        | 'tenant_name'
        | 'move_out_date'
        | 'damages_total'
    const sortDirection: 'asc' | 'desc' = ordering.startsWith('-')
        ? 'desc'
        : 'asc'

    const handleSort = useCallback(
        (field: 'tenant_name' | 'move_out_date' | 'damages_total') => {
            const isCurrentField = sortField === field
            const newOrdering: MoveOutOrdering =
                isCurrentField && sortDirection === 'asc'
                    ? (`-${field}` as MoveOutOrdering)
                    : field
            setOrdering(newOrdering)
            setPage(1)
        },
        [sortField, sortDirection],
    )

    const handleToggleBulkActions = useCallback(() => {
        setBulkActionsMode((prev) => {
            if (prev) setSelectedRows(new Set())
            return !prev
        })
    }, [])

    const handleToggleRow = useCallback((leaseId: number) => {
        setSelectedRows((prev) => {
            const next = new Set(prev)
            if (next.has(leaseId)) {
                next.delete(leaseId)
            } else {
                next.add(leaseId)
            }
            return next
        })
    }, [])

    const handleToggleAll = useCallback(() => {
        setSelectedRows((prev) => {
            if (prev.size === moveOutLeases.length) return new Set()
            return new Set(moveOutLeases.map((l) => l.id))
        })
    }, [moveOutLeases])

    const handleBulkSend = useCallback(() => {
        if (selectedRows.size === 0) return
        bulkSend.start(Array.from(selectedRows))
    }, [selectedRows, bulkSend])

    const handleBulkExport = useCallback(() => {
        if (selectedRows.size === 0) return
        bulkExport.start(Array.from(selectedRows))
    }, [selectedRows, bulkExport])

    const allSelected =
        moveOutLeases.length > 0 && selectedRows.size === moveOutLeases.length
    const someSelected =
        selectedRows.size > 0 && selectedRows.size < moveOutLeases.length

    const refundOwed = summaryStats
        ? summaryStats.total_deposits -
          summaryStats.total_damages -
          summaryStats.total_past_due_balances
        : null

    const statsCards = useMemo(() => {
        const moveOutsCard = {
            label: 'Total Move-Outs',
            value: summaryStats?.total_count ?? '—',
            icon: <EventIcon style={{ color: '#f59e0b' }} />,
            iconBg: 'rgba(245, 158, 11, 0.1)',
            valueColor: 'inherit',
        }
        const damagesCard = {
            label: 'Total Damages',
            value: summaryStats
                ? formatCurrency(summaryStats.total_damages)
                : '—',
            icon: <WarningIcon style={{ color: '#ef4444' }} />,
            iconBg: 'rgba(239, 68, 68, 0.1)',
            valueColor: 'inherit',
        }

        if (!hasFinancialConnection) {
            return [moveOutsCard, damagesCard]
        }

        return [
            moveOutsCard,
            {
                label: 'Total Deposits',
                value: summaryStats
                    ? formatCurrency(summaryStats.total_deposits)
                    : '—',
                icon: <AttachMoneyIcon style={{ color: '#3b82f6' }} />,
                iconBg: 'rgba(59, 130, 246, 0.1)',
                valueColor: 'inherit',
            },
            damagesCard,
            {
                label: 'Total Past Due',
                value: summaryStats
                    ? formatCurrency(summaryStats.total_past_due_balances)
                    : '—',
                icon: <AttachMoneyIcon style={{ color: '#f97316' }} />,
                iconBg: 'rgba(249, 115, 22, 0.1)',
                valueColor: 'inherit',
            },
            {
                label:
                    refundOwed !== null && refundOwed >= 0
                        ? 'Total Refunds to Tenants'
                        : 'Total Owed by Tenants',
                value:
                    refundOwed !== null
                        ? formatCurrency(Math.abs(refundOwed))
                        : '—',
                valueColor:
                    refundOwed !== null && refundOwed !== 0
                        ? refundOwed > 0
                            ? '#ef4444'
                            : '#10b981'
                        : 'inherit',
                icon:
                    refundOwed !== null && refundOwed >= 0 ? (
                        <ArrowDownwardIcon style={{ color: '#ef4444' }} />
                    ) : (
                        <ArrowUpwardIcon style={{ color: '#10b981' }} />
                    ),
                iconBg:
                    refundOwed !== null && refundOwed >= 0
                        ? 'rgba(239, 68, 68, 0.1)'
                        : 'rgba(16, 185, 129, 0.1)',
            },
        ]
    }, [summaryStats, hasFinancialConnection, refundOwed])

    return (
        <Box className={classes.root}>
            {/* Header */}
            <Box
                className={classes.headerSection}
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
            >
                <Box>
                    <Typography variant="h4" style={{ fontWeight: 'bold' }}>
                        Move-Out Management
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {hasFinancialConnection
                            ? 'Handle security deposit dispositions and final accounting'
                            : 'Manage final inspection results and move-out documentation'}
                    </Typography>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    style={{ gap: 4 }}
                >
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Button
                            variant={bulkActionsMode ? 'contained' : 'outlined'}
                            startIcon={<PlaylistAddCheckIcon />}
                            onClick={handleToggleBulkActions}
                            color={bulkActionsMode ? 'primary' : 'default'}
                        >
                            Bulk Actions
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<SettingsIcon />}
                            onClick={() => setConfigDialogOpen(true)}
                        >
                            Statement / Email Config
                        </Button>
                        {hasFinancialConnection ? (
                            <Button
                                variant="outlined"
                                startIcon={
                                    isSyncing ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <SyncIcon />
                                    )
                                }
                                onClick={startSync}
                                disabled={isSyncing}
                                style={{ minWidth: 185 }}
                            >
                                {isSyncing ? 'Syncing...' : 'Sync Transactions'}
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                className={classes.connectButton}
                                startIcon={
                                    <WarningIcon style={{ color: '#f59e0b' }} />
                                }
                                onClick={() => setConnectionDialogOpen(true)}
                            >
                                Connect Financial Data
                            </Button>
                        )}
                    </Box>
                    {hasFinancialConnection && (
                        <>
                            {syncProgress?.state === 'PROGRESS' &&
                            syncProgress.total ? (
                                <Box className={classes.syncProgressRow}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={
                                            ((syncProgress.current ?? 0) /
                                                syncProgress.total) *
                                            100
                                        }
                                        className={classes.syncProgressBar}
                                    />
                                    <Typography
                                        className={classes.syncProgressLabel}
                                    >
                                        {syncProgress.current ?? 0}/
                                        {syncProgress.total}
                                    </Typography>
                                </Box>
                            ) : syncProgress?.state === 'PENDING' ||
                              (syncProgress?.state === 'PROGRESS' &&
                                  !syncProgress.total) ? (
                                <Box className={classes.syncProgressRow}>
                                    <LinearProgress
                                        variant="indeterminate"
                                        className={classes.syncProgressBar}
                                    />
                                    <Typography
                                        className={classes.syncProgressLabel}
                                    >
                                        Starting...
                                    </Typography>
                                </Box>
                            ) : syncProgress?.state === 'FAILURE' ? (
                                <Typography
                                    className={classes.syncFailureCaption}
                                    onClick={resetProgress}
                                    style={{ cursor: 'pointer' }}
                                    title="Click to dismiss"
                                >
                                    Sync failed
                                    {syncProgress.error
                                        ? `: ${syncProgress.error}`
                                        : ''}
                                </Typography>
                            ) : (
                                <Typography className={classes.lastSyncCaption}>
                                    {lastSyncTime
                                        ? `Last synced: ${formatRelativeTime(
                                              lastSyncTime,
                                          )}`
                                        : 'Never synced'}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {/* Financial Connection Banner */}
            {!hasFinancialConnection && summaryStats && (
                <Box className={classes.financialBanner}>
                    <Box className={classes.financialBannerText}>
                        <WarningIcon
                            style={{ color: '#f59e0b', fontSize: 20 }}
                        />
                        <Typography
                            variant="body2"
                            style={{ color: '#92400e' }}
                        >
                            Financial data is unavailable — operating in Final
                            Inspection Result mode. Deposit, balance, and refund
                            information requires Entrata API access.
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        style={{
                            textTransform: 'none',
                            color: '#92400e',
                            whiteSpace: 'nowrap',
                        }}
                        onClick={() => setConnectionDialogOpen(true)}
                    >
                        Learn more
                    </Button>
                </Box>
            )}

            {/* Summary Cards */}
            <Grid container spacing={2}>
                {statsCards.map((stat) => (
                    <Grid item xs={6} md key={stat.label}>
                        <Card className={classes.statsCard}>
                            <CardContent>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 12 }}
                                >
                                    <Box
                                        className={classes.statsIconBox}
                                        style={{
                                            backgroundColor: stat.iconBg,
                                        }}
                                    >
                                        {stat.icon}
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {stat.label}
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            style={{
                                                fontWeight: 'bold',
                                                color: stat.valueColor,
                                            }}
                                        >
                                            {stat.value}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Filters */}
            <Box className={classes.filterRow}>
                <TextField
                    placeholder="Search by tenant or unit..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    variant="outlined"
                    size="small"
                    style={{ flex: 1, maxWidth: 400 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{ color: '#9ca3af' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    {hasFinancialConnection && (
                        <Chip
                            label="Balanced"
                            size="small"
                            variant={
                                quickFilters.balanced ? 'default' : 'outlined'
                            }
                            color={
                                quickFilters.balanced ? 'primary' : 'default'
                            }
                            onClick={() => handleToggleQuickFilter('balanced')}
                            style={{ cursor: 'pointer' }}
                        />
                    )}
                    <Chip
                        label="Has Damages"
                        size="small"
                        variant={
                            quickFilters.has_damages ? 'default' : 'outlined'
                        }
                        color={quickFilters.has_damages ? 'primary' : 'default'}
                        onClick={() => handleToggleQuickFilter('has_damages')}
                        style={{ cursor: 'pointer' }}
                    />
                    {hasFinancialConnection && (
                        <Chip
                            label="Has Past Due"
                            size="small"
                            variant={
                                quickFilters.has_past_due
                                    ? 'default'
                                    : 'outlined'
                            }
                            color={
                                quickFilters.has_past_due
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() =>
                                handleToggleQuickFilter('has_past_due')
                            }
                            style={{ cursor: 'pointer' }}
                        />
                    )}
                </Box>
                {bulkActionsMode && (
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: 12, marginLeft: 'auto' }}
                    >
                        <Typography
                            variant="body2"
                            style={{ color: '#6b7280', whiteSpace: 'nowrap' }}
                        >
                            <strong>{selectedRows.size}</strong> selected
                        </Typography>
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{ gap: 4 }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={
                                    bulkSend.isRunning ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <SendIcon />
                                    )
                                }
                                disabled={
                                    selectedRows.size === 0 ||
                                    bulkSend.isRunning ||
                                    bulkExport.isRunning
                                }
                                onClick={handleBulkSend}
                            >
                                {bulkSend.isRunning
                                    ? 'Sending...'
                                    : 'Bulk Send'}
                            </Button>
                            {bulkSend.isRunning && (
                                <Box className={classes.syncProgressRow}>
                                    {bulkSend.progress?.state === 'PROGRESS' &&
                                    bulkSend.progress.total ? (
                                        <>
                                            <LinearProgress
                                                variant="determinate"
                                                value={
                                                    ((bulkSend.progress
                                                        .current ?? 0) /
                                                        bulkSend.progress
                                                            .total) *
                                                    100
                                                }
                                                className={
                                                    classes.syncProgressBar
                                                }
                                            />
                                            <Typography
                                                className={
                                                    classes.syncProgressLabel
                                                }
                                            >
                                                {bulkSend.progress.current ?? 0}
                                                /{bulkSend.progress.total}
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <LinearProgress
                                                variant="indeterminate"
                                                className={
                                                    classes.syncProgressBar
                                                }
                                            />
                                            <Typography
                                                className={
                                                    classes.syncProgressLabel
                                                }
                                            >
                                                Starting...
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{ gap: 4 }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={
                                    bulkExport.isRunning ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <GetAppIcon />
                                    )
                                }
                                disabled={
                                    selectedRows.size === 0 ||
                                    bulkExport.isRunning ||
                                    bulkSend.isRunning
                                }
                                onClick={handleBulkExport}
                            >
                                {bulkExport.isRunning
                                    ? 'Exporting...'
                                    : 'Bulk Export'}
                            </Button>
                            {bulkExport.isRunning && (
                                <Box className={classes.syncProgressRow}>
                                    {bulkExport.progress?.state ===
                                        'PROGRESS' &&
                                    bulkExport.progress.total ? (
                                        <>
                                            <LinearProgress
                                                variant="determinate"
                                                value={
                                                    ((bulkExport.progress
                                                        .current ?? 0) /
                                                        bulkExport.progress
                                                            .total) *
                                                    100
                                                }
                                                className={
                                                    classes.syncProgressBar
                                                }
                                            />
                                            <Typography
                                                className={
                                                    classes.syncProgressLabel
                                                }
                                            >
                                                {bulkExport.progress.current ??
                                                    0}
                                                /{bulkExport.progress.total}
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <LinearProgress
                                                variant="indeterminate"
                                                className={
                                                    classes.syncProgressBar
                                                }
                                            />
                                            <Typography
                                                className={
                                                    classes.syncProgressLabel
                                                }
                                            >
                                                Starting...
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Loading State */}
            {isLoading && (
                <Box
                    display="flex"
                    justifyContent="center"
                    style={{ padding: 48 }}
                >
                    <CircularProgress />
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Card className={classes.statsCard}>
                    <CardContent>
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 8 }}
                        >
                            <WarningIcon style={{ color: '#ef4444' }} />
                            <Typography
                                variant="body2"
                                style={{ color: '#ef4444' }}
                            >
                                Failed to load move-out data. Please try again.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Move-Out Table */}
            {!isLoading && !error && (
                <Card className={classes.tableCard}>
                    <Box className={classes.tableScrollWrapper}>
                        <TableContainer style={{ flex: 1, overflow: 'auto' }}>
                            <Table stickyHeader>
                                <TableHead className={classes.tableHeader}>
                                    <TableRow>
                                        {bulkActionsMode && (
                                            <TableCell
                                                className={
                                                    classes.tableHeaderCell
                                                }
                                                padding="checkbox"
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={allSelected}
                                                    indeterminate={someSelected}
                                                    onChange={handleToggleAll}
                                                    color="primary"
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                        >
                                            <TableSortLabel
                                                active={
                                                    sortField === 'tenant_name'
                                                }
                                                direction={
                                                    sortField === 'tenant_name'
                                                        ? sortDirection
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort('tenant_name')
                                                }
                                            >
                                                Tenant
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                        >
                                            Unit
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                        >
                                            <TableSortLabel
                                                active={
                                                    sortField ===
                                                    'move_out_date'
                                                }
                                                direction={
                                                    sortField ===
                                                    'move_out_date'
                                                        ? sortDirection
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort('move_out_date')
                                                }
                                            >
                                                Move-Out Date
                                            </TableSortLabel>
                                        </TableCell>
                                        {hasFinancialConnection && (
                                            <TableCell
                                                className={
                                                    classes.tableHeaderCell
                                                }
                                            >
                                                Deposit
                                            </TableCell>
                                        )}
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                        >
                                            <TableSortLabel
                                                active={
                                                    sortField ===
                                                    'damages_total'
                                                }
                                                direction={
                                                    sortField ===
                                                    'damages_total'
                                                        ? sortDirection
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort('damages_total')
                                                }
                                            >
                                                Total Damages
                                            </TableSortLabel>
                                        </TableCell>
                                        {hasFinancialConnection && (
                                            <TableCell
                                                className={
                                                    classes.tableHeaderCell
                                                }
                                            >
                                                Past Due Balance
                                            </TableCell>
                                        )}
                                        {hasFinancialConnection && (
                                            <TableCell
                                                className={
                                                    classes.tableHeaderCell
                                                }
                                            >
                                                Refund to Tenant / Owed by
                                                Tenant
                                            </TableCell>
                                        )}
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                Status
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) =>
                                                        setStatusAnchorEl(
                                                            e.currentTarget,
                                                        )
                                                    }
                                                    style={{ marginLeft: 4 }}
                                                >
                                                    <FilterListIcon
                                                        style={{
                                                            fontSize: 16,
                                                            color:
                                                                statusFilters.length >
                                                                0
                                                                    ? '#3b82f6'
                                                                    : '#9ca3af',
                                                        }}
                                                    />
                                                </IconButton>
                                                {statusFilters.length > 0 && (
                                                    <Chip
                                                        label={
                                                            statusFilters.length
                                                        }
                                                        size="small"
                                                        style={{
                                                            height: 18,
                                                            fontSize: '0.65rem',
                                                            fontWeight: 700,
                                                            marginLeft: 2,
                                                            backgroundColor:
                                                                '#3b82f6',
                                                            color: '#fff',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Popover
                                                open={Boolean(statusAnchorEl)}
                                                anchorEl={statusAnchorEl}
                                                onClose={() =>
                                                    setStatusAnchorEl(null)
                                                }
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                            >
                                                <Box
                                                    p={1.5}
                                                    style={{ minWidth: 160 }}
                                                >
                                                    {ALL_STATUSES.map((s) => (
                                                        <FormControlLabel
                                                            key={s}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={statusFilters.includes(
                                                                        s,
                                                                    )}
                                                                    onChange={() =>
                                                                        handleToggleStatus(
                                                                            s,
                                                                        )
                                                                    }
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body2">
                                                                    {getStatusLabel(
                                                                        s,
                                                                        hasFinancialConnection,
                                                                    )}
                                                                </Typography>
                                                            }
                                                            style={{
                                                                display: 'flex',
                                                                margin: 0,
                                                            }}
                                                        />
                                                    ))}
                                                    {statusFilters.length >
                                                        0 && (
                                                        <Box
                                                            mt={1}
                                                            borderTop="1px solid #e5e7eb"
                                                            pt={1}
                                                        >
                                                            <Button
                                                                size="small"
                                                                onClick={() => {
                                                                    setStatusFilters(
                                                                        [],
                                                                    )
                                                                    setPage(1)
                                                                }}
                                                                style={{
                                                                    textTransform:
                                                                        'none',
                                                                    fontSize:
                                                                        '0.75rem',
                                                                }}
                                                            >
                                                                Clear all
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Popover>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                        >
                                            Last Sent
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {moveOutLeases.map((lease) => {
                                        const rowRefundOwed =
                                            parseFloat(lease.deposit || '0') -
                                            parseFloat(
                                                lease.total_damages || '0',
                                            ) -
                                            parseFloat(
                                                lease.past_due_balance || '0',
                                            )
                                        return (
                                            <TableRow
                                                key={lease.id}
                                                className={classes.tableRow}
                                                onClick={() => {
                                                    if (bulkActionsMode) {
                                                        handleToggleRow(
                                                            lease.id,
                                                        )
                                                    } else {
                                                        setSelectedLeaseId(
                                                            lease.id,
                                                        )
                                                    }
                                                }}
                                                selected={
                                                    bulkActionsMode &&
                                                    selectedRows.has(lease.id)
                                                }
                                            >
                                                {bulkActionsMode && (
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedRows.has(
                                                                lease.id,
                                                            )}
                                                            onChange={() =>
                                                                handleToggleRow(
                                                                    lease.id,
                                                                )
                                                            }
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                            color="primary"
                                                        />
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {lease.tenant_name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontFamily: 'monospace',
                                                    }}
                                                >
                                                    {lease.unit_name}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(
                                                        lease.move_out_date,
                                                    )}
                                                </TableCell>
                                                {hasFinancialConnection && (
                                                    <TableCell>
                                                        {formatCurrency(
                                                            parseFloat(
                                                                lease.deposit ||
                                                                    '0',
                                                            ),
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <span
                                                        className={
                                                            parseFloat(
                                                                lease.total_damages ||
                                                                    '0',
                                                            ) > 0
                                                                ? classes.amountPositive
                                                                : undefined
                                                        }
                                                    >
                                                        {formatCurrency(
                                                            parseFloat(
                                                                lease.total_damages ||
                                                                    '0',
                                                            ),
                                                        )}
                                                    </span>
                                                </TableCell>
                                                {hasFinancialConnection && (
                                                    <TableCell>
                                                        <span
                                                            className={
                                                                parseFloat(
                                                                    lease.past_due_balance ||
                                                                        '0',
                                                                ) > 0
                                                                    ? classes.amountPositive
                                                                    : undefined
                                                            }
                                                        >
                                                            {formatCurrency(
                                                                parseFloat(
                                                                    lease.past_due_balance ||
                                                                        '0',
                                                                ),
                                                            )}
                                                        </span>
                                                    </TableCell>
                                                )}
                                                {hasFinancialConnection && (
                                                    <TableCell>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            style={{
                                                                gap: 4,
                                                                color:
                                                                    rowRefundOwed ===
                                                                    0
                                                                        ? 'inherit'
                                                                        : rowRefundOwed >
                                                                          0
                                                                        ? '#ef4444'
                                                                        : '#10b981',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {rowRefundOwed !==
                                                                0 &&
                                                                (rowRefundOwed >
                                                                0 ? (
                                                                    <ArrowDownwardIcon
                                                                        style={{
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <ArrowUpwardIcon
                                                                        style={{
                                                                            fontSize: 16,
                                                                        }}
                                                                    />
                                                                ))}
                                                            {formatCurrency(
                                                                Math.abs(
                                                                    rowRefundOwed,
                                                                ),
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    {getStatusChip(
                                                        lease.final_move_out_status,
                                                        hasFinancialConnection,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {lease.last_fmo_sent_date ? (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            style={{ gap: 6 }}
                                                        >
                                                            <Typography variant="body2">
                                                                {formatDate(
                                                                    lease.last_fmo_sent_date,
                                                                )}
                                                            </Typography>
                                                            {lease.last_fmo_pdf_url && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation()
                                                                        const prefix = hasFinancialConnection
                                                                            ? 'FMO'
                                                                            : 'FIR'
                                                                        setViewingDocument(
                                                                            {
                                                                                id:
                                                                                    lease.id,
                                                                                name: `${prefix}-${lease.unit_name}-${lease.tenant_name}.pdf`,
                                                                                url: lease.last_fmo_pdf_url!,
                                                                            },
                                                                        )
                                                                    }}
                                                                    title="View sent PDF"
                                                                >
                                                                    <VisibilityIcon
                                                                        style={{
                                                                            fontSize: 18,
                                                                            color:
                                                                                '#6b7280',
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            —
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                    {moveOutLeases.length === 0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={
                                                    (hasFinancialConnection
                                                        ? 9
                                                        : 6) +
                                                    (bulkActionsMode ? 1 : 0)
                                                }
                                                align="center"
                                                style={{ padding: 32 }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {searchQuery ||
                                                    statusFilters.length > 0 ||
                                                    Object.keys(quickFilters)
                                                        .length > 0
                                                        ? 'No move-outs match your filters'
                                                        : 'No move-out leases found'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {isFetching && !isLoading && (
                            <Box
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255,255,255,0.6)',
                                    zIndex: 1,
                                }}
                            >
                                <CircularProgress size={40} />
                            </Box>
                        )}
                    </Box>
                    <TablePagination
                        component="div"
                        count={count}
                        page={page - 1}
                        onChangePage={handlePageChange}
                        rowsPerPage={pageSize}
                        onChangeRowsPerPage={handlePageSizeChange}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelDisplayedRows={({ from, to, count: total }) =>
                            `${from}-${to} of ${total}`
                        }
                        labelRowsPerPage="Rows per page:"
                    />
                </Card>
            )}
            <MoveOutDetail
                open={selectedLeaseId !== null}
                leaseId={selectedLeaseId}
                onClose={() => setSelectedLeaseId(null)}
            />
            <DocumentViewer
                open={!!viewingDocument}
                documentId={viewingDocument?.id ?? null}
                documentName={viewingDocument?.name}
                documentUrl={viewingDocument?.url}
                onClose={() => setViewingDocument(null)}
            />

            <FMOConfigDialog
                open={configDialogOpen}
                onClose={() => setConfigDialogOpen(false)}
            />

            <FinancialConnectionDialog
                open={connectionDialogOpen}
                onClose={() => setConnectionDialogOpen(false)}
            />
        </Box>
    )
}
